import type { BiddingCase, AnnotationData, User, DatasetExportTask, ApiEndpoint, ApiKey, FinetuneTask } from '../types/index'

// Mock用户数据
export const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    email: 'admin@example.com',
    role: 'admin',
    status: 'active',
    lastLoginAt: '2026-02-22 15:30:00',
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    username: 'annotator1',
    email: 'annotator1@example.com',
    role: 'annotator',
    status: 'active',
    lastLoginAt: '2026-02-22 14:20:00',
    createdAt: '2024-01-15',
    stats: {
      totalCases: 156,
      accuracy: 98,
    },
  },
  {
    id: '3',
    username: 'reviewer1',
    email: 'reviewer1@example.com',
    role: 'reviewer',
    status: 'active',
    lastLoginAt: '2026-02-22 13:45:00',
    createdAt: '2024-02-01',
    stats: {
      totalCases: 89,
      accuracy: 96,
    },
  },
  {
    id: '4',
    username: 'query1',
    email: 'query1@example.com',
    role: 'query_user',
    status: 'active',
    lastLoginAt: '2026-02-22 10:30:00',
    createdAt: '2024-03-01',
  },
]

// 基于docs目录下的真实数据构建Mock案件
export const mockCases: BiddingCase[] = [
  {
    caseId: 'CASE-2024-0001',
    projectName: '崇左某单位副食品配送项目',
    status: 'annotating',
    questionDoc: {
      docId: 'DOC-001-Q',
      docType: 'question',
      fileName: '质疑函.pdf',
      filePath: '/docs/case-001/question.pdf',
      fileSize: 1153994,
      pageCount: 3,
      fileFormat: 'pdf',
      cleaningStatus: 'completed',
      cleaningProgress: 100,
      cleaningSteps: [],
    },
    replyDoc: {
      docId: 'DOC-001-R',
      docType: 'reply',
      fileName: '答复函.pdf',
      filePath: '/docs/case-001/reply.pdf',
      fileSize: 893104,
      pageCount: 2,
      fileFormat: 'pdf',
      cleaningStatus: 'completed',
      cleaningProgress: 100,
      cleaningSteps: [],
    },
    pairingInfo: {
      method: 'auto_folder',
      pairedAt: '2026-02-22 10:00:00',
    },
    createdAt: '2026-02-22',
    updatedAt: '2026-02-22',
  },
  {
    caseId: 'CASE-2024-0002',
    projectName: '云南烟叶复烤有限责任公司楚雄复烤厂智慧消防建设项目',
    status: 'reviewing',
    questionDoc: {
      docId: 'DOC-002-Q',
      docType: 'question',
      fileName: '质疑函.pdf',
      filePath: '/docs/case-002/question.pdf',
      fileSize: 1250000,
      pageCount: 4,
      fileFormat: 'pdf',
      cleaningStatus: 'completed',
      cleaningProgress: 100,
      cleaningSteps: [],
    },
    replyDoc: {
      docId: 'DOC-002-R',
      docType: 'reply',
      fileName: '合肥科大立安安全技术有限责任公司-质疑答复.pdf',
      filePath: '/docs/case-002/reply.pdf',
      fileSize: 980000,
      pageCount: 3,
      fileFormat: 'pdf',
      cleaningStatus: 'completed',
      cleaningProgress: 100,
      cleaningSteps: [],
    },
    pairingInfo: {
      method: 'auto_ai',
      confidence: 95,
      matchedBy: ['文号匹配', '项目名称匹配'],
      pairedAt: '2026-02-21 15:30:00',
    },
    createdAt: '2026-02-21',
    updatedAt: '2026-02-22',
  },
]

// 基于docs目录下的真实标注数据
export const mockAnnotationData: Record<string, AnnotationData> = {
  // CASE-2024-0001: 崇左某单位副食品配送项目 - 质疑函数据
  'CASE-2024-0001': {
    caseId: 'CASE-2024-0001',
    docMeta: {
      doc_type: '质疑函',
      case_id: 'CASE-2024-0001',
      doc_id: '',
      project_name: '崇左某单位副食品配送项目',
      issue_date: '2024-05-20',
      intent_summary: '对中标结果提出质疑，认为中标人报价不一致、疑似弄虚作假且明显低于成本价。',
      overall_issue_types: ['报价评审', '中标结果'],
      expected_outcome: '取消广西盛世鸿源商贸有限公司中标资格',
      responder: {
        name: '崇左某单位',
        role: '招标人',
      },
      questioner: {
        name: '广西裕达农副产品配送有限公司',
        role: '投标人',
      },
      tender_agent: {
        name: '广西同泽工程项目管理股份有限公司',
        role: '代理机构',
      },
      platform: '',
    },
    questionDetails: [
      {
        item_id: 'CASE-2024-0001-Q-01',
        index: 1,
        topic: '报价评审',
        question_summary: '中标人公告报价与现场唱标折扣率不一致',
        question_detail: '现场开标时投影仪显示广西盛世鸿源商贸有限公司综合折扣率为95%，但结果公告显示为4.75%，存在明显差异。',
        requested_action: '取消广西盛世鸿源商贸有限公司中标资格',
        evidence_facts: [
          '开标现场投影仪显示的综合折扣率为95%',
          '中标结果公告中显示的折扣率为4.75%',
        ],
        laws_regulations_cited: [
          {
            name: '评标委员会和评标方法暂行规定',
            articles: [
              {
                article_index: '第二十一条',
                content: '在评标过程中，评标委员会发现投标人的报价明显低于其他投标报价，或者在设有标底时明显低于标底，使得其投标报价可能低于其个别成本的，应当要求该投标人作出书面说明并提供相关证明材料。',
              },
            ],
          },
          {
            name: '中华人民共和国招标投标法实施条例',
            articles: [
              {
                article_index: '第五十一条',
                content: '有下列情形之一的，评标委员会应当否决其投标：...（具体情形列举）',
              },
            ],
          },
        ],
      },
      {
        item_id: 'CASE-2024-0001-Q-02',
        index: 2,
        topic: '程序流程',
        question_summary: '怀疑存在调换报价文件等弄虚作假行为',
        question_detail: '质疑方认为可能存在调换报价文件的行为，影响公平公正。',
        requested_action: '调查是否存在调换报价文件并纠正评标结果',
        evidence_facts: ['现场唱标结果与公告结果存在明显差异'],
        laws_regulations_cited: [
          {
            name: '中华人民共和国招标投标法实施条例',
            articles: [
              {
                article_index: '第五十一条',
                content: '',
              },
            ],
          },
        ],
      },
      {
        item_id: 'CASE-2024-0001-Q-03',
        index: 3,
        topic: '报价评审',
        question_summary: '中标报价明显低于成本价',
        question_detail: '中标公司最终报价综合折扣率为95%，货款金额仅为45000元，明显低于运营成本。',
        requested_action: '认定其报价为异常低价并取消中标资格',
        evidence_facts: [
          '综合折扣率为95%',
          '货款金额约45000元，低于合理运营成本',
        ],
        laws_regulations_cited: [
          {
            name: '评标委员会和评标方法暂行规定',
            articles: [
              {
                article_index: '第二十一条',
                content: '',
              },
            ],
          },
        ],
      },
      {
        item_id: 'CASE-2024-0001-Q-04',
        index: 4,
        topic: '中标结果',
        question_summary: '与其他投标人相比存在恶意低价竞标嫌疑',
        question_detail: '与其他投标公司相比（广西绿源供应链管理有限责任公司综合折扣率0.6%，广西裕达农副产品配送有限公司综合折扣率1%），中标公司报价明显过低。',
        requested_action: '对恶意低价行为作出处理并重新评标',
        evidence_facts: [
          '其他投标人折扣率为0.6%和1%',
          '中标人折扣率为95%',
        ],
        laws_regulations_cited: [],
      },
    ],
    replyDetails: [
      {
        item_id: 'CASE-2024-0001-R-01',
        related_inquiry_item_id: 'CASE-2024-0001-Q-01',
        topic: '报价评审',
        question_summary: '中标人公告报价与现场唱标折扣率不一致',
        tender_clauses_cited: ['招标文件第三章评标办法明确规定...'],
        laws_regulations_cited: [
          {
            name: '中华人民共和国招标投标法实施条例',
            articles: [
              {
                article_index: '第五十二条',
                content: '评标委员会成员应当客观、公正地履行职务...',
              },
            ],
          },
        ],
        reply_reasoning: '经核查，现场唱标记录与公告结果一致，质疑方所提及的"95%"为投影显示错误，实际唱标记录为4.75%。评标过程符合法定程序，不存在报价不一致的情况。',
        reply_conclusion: '维持原中标结果，不予修改。',
        reply_position: '不采纳',
      },
      {
        item_id: 'CASE-2024-0001-R-02',
        related_inquiry_item_id: 'CASE-2024-0001-Q-02',
        topic: '程序流程',
        question_summary: '怀疑存在调换报价文件等弄虚作假行为',
        tender_clauses_cited: ['开标过程全程录像', '评标委员会监督记录'],
        laws_regulations_cited: [
          {
            name: '中华人民共和国招标投标法',
            articles: [
              {
                article_index: '第三十六条',
                content: '开标时，由投标人或者其推选的代表检查投标文件的密封情况...',
              },
            ],
          },
        ],
        reply_reasoning: '经调阅开标现场录像及评标监督记录，整个开标过程公开透明，有多方监督人员在场，未发现调换报价文件的情况。',
        reply_conclusion: '质疑不成立，维持原评标结果。',
        reply_position: '不采纳',
      },
      {
        item_id: 'CASE-2024-0001-R-03',
        related_inquiry_item_id: 'CASE-2024-0001-Q-03',
        topic: '报价评审',
        question_summary: '中标报价明显低于成本价',
        tender_clauses_cited: ['招标文件关于报价评审的条款'],
        laws_regulations_cited: [
          {
            name: '评标委员会和评标方法暂行规定',
            articles: [
              {
                article_index: '第二十一条',
                content: '在评标过程中，评标委员会发现投标人的报价明显低于其他投标报价...',
              },
            ],
          },
        ],
        reply_reasoning: '中标人已提供书面说明及相关证明材料，证明其报价基于优化供应链管理、降低运营成本等合理因素。评标委员会经评审认为其报价合理。',
        reply_conclusion: '认可中标人报价，维持中标结果。',
        reply_position: '不采纳',
      },
    ],
    entities: [
      { id: 'e1', text: '广西裕达农副产品配送有限公司', label: 'ORG_BIDDER', start: 0, end: 20 },
      { id: 'e2', text: '崇左某单位副食品配送项目', label: 'PROJECT', start: 0, end: 20 },
      { id: 'e3', text: '广西盛世鸿源商贸有限公司', label: 'ORG_BIDDER', start: 0, end: 20 },
      { id: 'e4', text: '95%', label: 'RATE', start: 0, end: 5 },
      { id: 'e5', text: '4.75%', label: 'RATE', start: 0, end: 6 },
      { id: 'e6', text: '评标委员会和评标方法暂行规定', label: 'LAW', start: 0, end: 20 },
      { id: 'e7', text: '第二十一条', label: 'LAW_ARTICLE', start: 0, end: 6 },
      { id: 'e8', text: '45000元', label: 'MONEY', start: 0, end: 8 },
    ],
    relations: [
      { id: 'r1', head: '广西裕达农副产品配送有限公司', head_type: 'ORG_BIDDER', relation: 'involved_in', tail: '崇左某单位副食品配送项目', tail_type: 'PROJECT' },
      { id: 'r2', head: '广西裕达农副产品配送有限公司', head_type: 'ORG_BIDDER', relation: 'raises_objection', tail: '报价不一致', tail_type: 'RISK_POINT' },
    ],
    allLaws: [
      { name: '评标委员会和评标方法暂行规定', version_date: '', scope: '部门规章', articles: [] },
      { name: '中华人民共和国招标投标法实施条例', version_date: '', scope: '行政法规', articles: [] },
    ],
    version: 1,
    createdAt: '2026-02-22',
    updatedAt: '2026-02-22',
    createdBy: 'annotator1',
    updatedBy: 'annotator1',
  },
  
  // CASE-2024-0002: 云南烟叶智慧消防建设项目 - 质疑答复函数据
  'CASE-2024-0002': {
    caseId: 'CASE-2024-0002',
    docMeta: {
      doc_type: '质疑答复函',
      case_id: 'CASE-2024-0002',
      doc_id: 'WH-530100-2024-0278-0101',
      project_name: '云南烟叶复烤有限责任公司楚雄复烤厂智慧消防建设项目',
      issue_date: '2024-09-27',
      intent_summary: '对投标人就资质条件、项目管理人员要求及技术参数设置提出的质疑逐条作出解释和处理意见。',
      overall_decision: '维持原招标要求和评标结果，对部分表述作解释说明',
      responder: {
        name: '云南烟叶复烤有限责任公司楚雄复烤厂',
        role: '招标人',
      },
      questioner: {
        name: '合肥科大立安安全技术有限责任公司',
        role: '投标人',
      },
      tender_agent: {
        name: '广西同泽工程项目管理股份有限公司',
        role: '代理机构',
      },
      platform: '中国招标投标公共服务平台',
    },
    questionDetails: [
      {
        item_id: 'CASE-2024-0002-Q-01',
        index: 1,
        topic: '资格条件',
        question_summary: '质疑投标人资格要求中对企业资质设定存在超越资质范围情形',
        question_detail: '投标人资格要求中对投标人企业资质设定不能包含本项目开展的全部建设内容，存在超越资质范围的情形，且不符合《云南烟草商业智慧消防系统联网建设规范（试行）实操指南》对资质的设置要求。',
        requested_action: '修改招标文件中的资质要求',
        evidence_facts: [
          '招标文件要求消防设施工程专业承包二级及以上资质',
          '质疑方认为该资质不能覆盖全部建设内容',
        ],
        laws_regulations_cited: [
          {
            name: '云南烟草商业智慧消防系统联网建设规范（试行）实操指南',
            articles: [],
          },
        ],
      },
      {
        item_id: 'CASE-2024-0002-Q-02',
        index: 2,
        topic: '人员要求',
        question_summary: '质疑未对项目负责人等管理人员提出资格要求',
        question_detail: '本项目为工程施工招标项目，但未对投标人资格要求中的项目负责人、技术负责人、安全员等重要工程项目管理人员进行人员资格要求。',
        requested_action: '补充人员资格要求',
        evidence_facts: [
          '招标文件未明确项目负责人资格要求',
          '未明确技术负责人、安全员资格要求',
        ],
        laws_regulations_cited: [
          {
            name: '中华人民共和国建筑法',
            articles: [
              { article_index: '第十三条', content: '从事建筑活动的建筑施工企业...' },
              { article_index: '第十四条', content: '从事建筑活动的专业技术人员...' },
            ],
          },
        ],
      },
      {
        item_id: 'CASE-2024-0002-Q-03',
        index: 3,
        topic: '技术参数',
        question_summary: '质疑部分技术参数存在排他性、唯一性问题',
        question_detail: '招标文件中★号技术要求条款存在限制性、排他性、唯一性、与规范不符及与招标人实际使用场景不匹配等问题。',
        requested_action: '修改技术参数要求',
        evidence_facts: [
          '★号条款存在排他性',
          '部分参数与规范不符',
        ],
        laws_regulations_cited: [
          {
            name: '云南省招标投标条例',
            articles: [
              { article_index: '第二十一条', content: '招标人不得以不合理的条件限制或者排斥潜在投标人...' },
            ],
          },
        ],
      },
    ],
    replyDetails: [
      {
        item_id: 'CASE-2024-0002-ITEM-01',
        related_inquiry_item_id: 'CASE-2024-0002-Q-01',
        topic: '资格条件',
        question_summary: '质疑投标人资格要求中对企业资质设定存在超越资质范围情形，且不符合相关规范。',
        tender_clauses_cited: [
          '投标人资格要求: 投标人具备建设行政主管部门颁发的消防设施工程专业承包二级及以上（含二级）资质，并具有有效的安全生产许可证',
        ],
        laws_regulations_cited: [
          {
            name: '云南烟草商业智慧消防系统建设规范（试行）',
            articles: [],
          },
          {
            name: '烟草建筑消防设计规范',
            version_date: 'DBJ53-13-2012',
            articles: [
              { article_index: '', content: '烟草建筑生产及储存的火灾危险性类别为丙类' },
            ],
          },
        ],
        reply_reasoning: '招标文件内容依据《云南烟草商业智慧消防系统建设规范（试行）》、《实操指南》等建设性指导文件，属于智慧消防建设范畴。根据DBJ53-13-2012《烟草建筑消防设计规范》规定烟草建筑生产及储存的火灾危险性类别为丙类，消防设施工程专业承包二级资质可承包范围为火灾危险性丙类以下的厂房、仓库、储罐、堆场。因此，招标人设定的资质要求符合项目实际和规范要求，不存在超越资质范围的情形。',
        reply_conclusion: '维持原招标文件中的投标人资格要求，不予修改。',
        reply_position: '不采纳',
      },
      {
        item_id: 'CASE-2024-0002-ITEM-02',
        related_inquiry_item_id: 'CASE-2024-0002-Q-02',
        topic: '人员要求',
        question_summary: '质疑未对项目负责人等管理人员提出资格要求。',
        tender_clauses_cited: [],
        laws_regulations_cited: [
          {
            name: '中华人民共和国建筑法',
            version_date: '2019修正',
            articles: [
              { article_index: '第十三条', content: '从事建筑活动的建筑施工企业、勘察单位、设计单位和工程监理单位，按照其拥有的注册资本、专业技术人员、技术装备和已完成的建筑工程业绩等资质条件...' },
              { article_index: '第十四条', content: '从事建筑活动的专业技术人员，应当依法取得相应的执业资格证书，并在执业资格证书许可的范围内从事建筑活动。' },
            ],
          },
        ],
        reply_reasoning: '虽然招标文件在资格审查环节未强制要求列出所有管理人员，但依据《建筑法》相关规定，施工企业必须具备相应资质及持证上岗的专业技术人员。招标人将在后续合同签订及履约管理阶段，严格要求中标人按国家规定配备项目管理机构人员。',
        reply_conclusion: '不调整招标文件资格条件，但在项目实施阶段加强人员履约管理。',
        reply_position: '部分采纳',
      },
      {
        item_id: 'CASE-2024-0002-ITEM-03',
        related_inquiry_item_id: 'CASE-2024-0002-Q-03',
        topic: '技术参数',
        question_summary: '质疑部分技术参数存在排他性、唯一性问题。',
        tender_clauses_cited: [
          '技术参数要求: （注：此处需补全相关★号条款原文）',
        ],
        laws_regulations_cited: [
          {
            name: '云南省招标投标条例',
            articles: [
              { article_index: '第二十一条', content: '招标人不得以不合理的条件限制或者排斥潜在投标人...' },
            ],
          },
        ],
        reply_reasoning: '经核查，大部分★号条款系根据项目特殊需求设定，符合法律法规关于招标人自主确定技术需求的权利。对于个别描述可能产生歧义的条款，招标人现予以澄清说明，以确保公平竞争。',
        reply_conclusion: '对部分技术参数进行澄清说明，其余维持原状。',
        reply_position: '部分采纳',
      },
    ],
    entities: [
      { id: 'e1', text: '合肥科大立安安全技术有限责任公司', label: 'ORG_BIDDER', start: 0, end: 20 },
      { id: 'e2', text: '云南烟叶复烤有限责任公司楚雄复烤厂智慧消防建设项目', label: 'PROJECT', start: 0, end: 30 },
      { id: 'e3', text: '云南烟叶复烤有限责任公司楚雄复烤厂', label: 'ORG_BUYER', start: 0, end: 20 },
      { id: 'e4', text: '中华人民共和国建筑法', label: 'LAW', start: 0, end: 15 },
      { id: 'e5', text: '第十三条', label: 'LAW_ARTICLE', start: 0, end: 5 },
      { id: 'e6', text: '第十四条', label: 'LAW_ARTICLE', start: 0, end: 5 },
      { id: 'e7', text: '云南省招标投标条例', label: 'LAW', start: 0, end: 10 },
      { id: 'e8', text: '第二十一条', label: 'LAW_ARTICLE', start: 0, end: 5 },
      { id: 'e9', text: '烟草建筑消防设计规范', label: 'LAW', start: 0, end: 12 },
    ],
    relations: [
      { id: 'r1', head: '合肥科大立安安全技术有限责任公司', head_type: 'ORG_BIDDER', relation: 'involved_in', tail: '云南烟叶复烤有限责任公司楚雄复烤厂智慧消防建设项目', tail_type: 'PROJECT' },
      { id: 'r2', head: '合肥科大立安安全技术有限责任公司', head_type: 'ORG_BIDDER', relation: 'raises_objection', tail: '资质超越范围', tail_type: 'RISK_POINT' },
      { id: 'r3', head: '烟草建筑消防设计规范', head_type: 'LAW', relation: 'refutes', tail: '资质超越范围', tail_type: 'RISK_POINT' },
    ],
    allLaws: [
      { name: '中华人民共和国建筑法', version_date: '2019修正', scope: '法律', articles: [] },
      { name: '云南省招标投标条例', version_date: '', scope: '地方性法规', articles: [] },
      { name: '烟草建筑消防设计规范', version_date: 'DBJ53-13-2012', scope: '规范性文件', articles: [] },
    ],
    version: 1,
    createdAt: '2026-02-22',
    updatedAt: '2026-02-22',
    createdBy: 'annotator1',
    updatedBy: 'reviewer1',
  },
}

// Mock数据集导出任务
export const mockDatasetTasks: DatasetExportTask[] = [
  {
    taskId: 'DL-20260222-001',
    taskName: '资质质疑专项数据集',
    createdAt: '2026-02-22 15:30:00',
    createdBy: 'admin',
    filters: {
      topics: ['资格条件'],
      qualityLevel: ['excellent', 'good'],
    },
    exportConfig: {
      contents: ['doc_meta', 'question_details', 'reply_details'],
      formats: ['jsonl'],
      sftFormat: 'alpaca',
      includeCoT: true,
      separateRationale: true,
      desensitizationLevel: 'none',
    },
    status: 'completed',
    progress: 100,
    totalCases: 500,
    fileSize: 2469600000,
    downloadUrl: '/api/download/DL-20260222-001',
    expiresAt: '2026-03-22 15:30:00',
  },
]

// Mock API端点
export const mockApiEndpoints: ApiEndpoint[] = [
  {
    id: 'api-1',
    name: '案件查询接口',
    method: 'GET',
    path: '/api/v1/cases',
    description: '查询案件列表，支持分页和筛选',
    status: 'online',
    currentQps: 45,
    maxQps: 100,
    avgLatency: 35,
    errorRate: 0.2,
    authRequired: true,
    rateLimit: 100,
    timeout: 30,
    cacheEnabled: true,
    cacheTtl: 300,
  },
  {
    id: 'api-2',
    name: '预标注接口',
    method: 'POST',
    path: '/api/v1/pre-annotate',
    description: '对文本进行智能预标注',
    status: 'degraded',
    currentQps: 3,
    maxQps: 5,
    avgLatency: 2500,
    errorRate: 2.5,
    authRequired: true,
    rateLimit: 5,
    timeout: 60,
    cacheEnabled: false,
  },
]

// Mock API密钥
export const mockApiKeys: ApiKey[] = [
  {
    keyId: 'ak_abc123x8s2d',
    keyName: '内部系统-生产',
    keySecret: 'ak_****x8s2d',
    createdAt: '2026-01-15 10:30:00',
    expiresAt: '2027-01-15 10:30:00',
    permissions: ['read', 'write'],
    allowedIps: ['192.168.1.0/24', '10.0.0.0/8'],
    rateLimit: 100,
    usageStats: {
      totalCalls: 125890,
      lastUsedAt: '2026-02-22 15:28:15',
    },
    status: 'active',
  },
]

// Mock微调任务
export const mockFinetuneTasks: FinetuneTask[] = [
  {
    taskId: 'FT-20260222-001',
    taskName: 'v1.2-资质质疑-001',
    baseModel: 'Qwen-14B-Chat',
    dataConfig: {
      source: 'platform',
      filters: {
        topics: ['资格条件'],
        qualityLevel: 'excellent',
      },
      format: 'sft',
    },
    trainingConfig: {
      method: 'lora',
      loraConfig: {
        r: 64,
        alpha: 128,
        dropout: 0.05,
        targetModules: ['q_proj', 'v_proj', 'k_proj', 'o_proj'],
      },
      trainingArgs: {
        epochs: 3,
        batchSize: 4,
        learningRate: 0.0002,
        maxSeqLength: 2048,
        warmupSteps: 100,
        gradientAccumulation: 4,
        weightDecay: 0.01,
      },
      optimizer: 'AdamW',
      lrScheduler: 'Cosine',
      precision: 'bf16',
    },
    resourceConfig: {
      gpuType: 'A100',
      gpuCount: 4,
      deepspeed: 'ZeRO-2',
      optimizations: ['Flash Attention 2', 'Gradient Checkpointing'],
    },
    status: 'completed',
    progress: {
      currentStep: 1860,
      totalSteps: 1860,
      currentEpoch: 3,
      totalEpochs: 3,
      percentage: 100,
    },
    metrics: {
      trainLoss: 0.32,
      valLoss: 0.35,
      perplexity: 1.38,
      learningRate: 0.00001,
    },
    outputModel: {
      modelId: 'model-001',
      modelName: 'bidding-v1.2-qualification',
      modelPath: '/models/bidding-v1.2-qualification',
      modelSize: 28547456728,
      isDeployed: true,
    },
    createdAt: '2026-02-22 10:00:00',
    startedAt: '2026-02-22 10:05:00',
    completedAt: '2026-02-22 12:35:00',
    estimatedTime: 9000,
  },
  {
    taskId: 'FT-20260222-002',
    taskName: 'v1.2-报价评审-002',
    baseModel: 'Qwen-14B-Chat',
    dataConfig: {
      source: 'platform',
      filters: {
        topics: ['报价评审'],
      },
      format: 'sft',
    },
    trainingConfig: {
      method: 'lora',
      loraConfig: {
        r: 64,
        alpha: 128,
        dropout: 0.05,
        targetModules: ['q_proj', 'v_proj', 'k_proj', 'o_proj'],
      },
      trainingArgs: {
        epochs: 3,
        batchSize: 4,
        learningRate: 0.0002,
        maxSeqLength: 2048,
        warmupSteps: 100,
        gradientAccumulation: 4,
        weightDecay: 0.01,
      },
      optimizer: 'AdamW',
      lrScheduler: 'Cosine',
      precision: 'bf16',
    },
    resourceConfig: {
      gpuType: 'A100',
      gpuCount: 4,
      deepspeed: 'ZeRO-2',
      optimizations: ['Flash Attention 2', 'Gradient Checkpointing'],
    },
    status: 'running',
    progress: {
      currentStep: 1247,
      totalSteps: 1860,
      currentEpoch: 2,
      totalEpochs: 3,
      percentage: 67,
    },
    metrics: {
      trainLoss: 0.45,
      learningRate: 0.00012,
    },
    createdAt: '2026-02-22 13:00:00',
    startedAt: '2026-02-22 13:05:00',
    estimatedTime: 5400,
    logs: [
      '2026-02-22 15:28:15 [INFO] Step 1247/1860, Loss: 0.452, LR: 1.2e-4',
      '2026-02-22 15:28:10 [INFO] Step 1245/1860, Loss: 0.458, LR: 1.3e-4',
    ],
  },
]
